import { useAuthentication } from '@givebacks/api';
import { useStoreApi } from '@givebacks/api/store';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Subscription } from '../new/types/subscriptions';

type SubscriptionSearches = {
  active: string;
};

const useGetSubscriptions = (search?: keyof SubscriptionSearches) => {
  const StoreApi = useStoreApi();
  const searches = {
    active: '?search[status][value]=active',
  };
  const getSubscriptions = async (): Promise<Subscription[]> => {
    const { data } = await StoreApi.get(`/subscriptions${search ? searches[search] : ''}`);
    return data.subscriptions;
  };
  return getSubscriptions;
};

export function useListUserSubscriptions<T = Subscription[]>(select?: (data: Subscription[]) => T) {
  const { isAuthenticated } = useAuthentication();
  const getSubscription = useGetSubscriptions();

  return useQuery({
    queryKey: ['user', 'store', 'subscriptions'],
    queryFn: getSubscription,
    enabled: !!isAuthenticated,
    select,
  });
}
// The main reason this is separate is because the API response is different than a normal response.
// We could just manipulate the query key in useListUserSubscriptions, but this is more explicit and still allows us to
// Keep the current select functionality. If we were just selecting the data where data.sub == active we would just use the above, but this is a new query
export function useListUserActiveSubscriptions<T = Subscription[]>(
  select?: (data: Subscription[]) => T
) {
  const { isAuthenticated } = useAuthentication();
  const getActiveSubscriptions = useGetSubscriptions('active');

  return useQuery({
    queryKey: ['user', 'store', 'subscriptions', 'active'],
    queryFn: getActiveSubscriptions,
    enabled: !!isAuthenticated,
    select,
  });
}

export function useShowUserSubscription(subscription_uuid?: string) {
  const StoreApi = useStoreApi();
  const getSubscription = async (): Promise<Subscription> => {
    const { data } = await StoreApi.get(`subscriptions/${subscription_uuid}`);
    return data.subscription;
  };

  return useQuery({
    queryKey: ['user', 'store', 'subscriptions', subscription_uuid],
    queryFn: getSubscription,
    enabled: !!subscription_uuid,
  });
}

export const useCancelCartSubscription = () => {
  const StoreApi = useStoreApi();
  const queryClient = useQueryClient();
  const cancelCartSubscription = async ({
    cart_id,
    cause_id,
  }: {
    cart_id: string;
    cause_id: string;
  }) => {
    const { data } = await StoreApi.delete(
      `/subscriptions/${cart_id}?cart=true&cause_id=${cause_id}`
    );

    return data.subscription;
  };

  return useMutation({
    mutationFn: cancelCartSubscription,
    onSuccess(data, variables, context) {
      return queryClient.invalidateQueries({
        queryKey: ['user', 'store', 'subscriptions', data.uuid],
      });
    },
  });
};

export const useCancelUserSubscription = () => {
  const StoreApi = useStoreApi();
  const queryClient = useQueryClient();
  const cancelCartSubscription = async (sub_id: string) => {
    const { data } = await StoreApi.delete(`/subscriptions/${sub_id}`);
    return data.subscription;
  };

  return useMutation({
    mutationFn: cancelCartSubscription,
    onSuccess(data, variables, context) {
      queryClient.invalidateQueries({
        queryKey: ['user', 'store', 'subscriptions', data.uuid],
      });
      queryClient.invalidateQueries({
        queryKey: ['user', 'store', 'subscriptions'],
      });
    },
  });
};
